import {ReactNode} from 'react';
import {FilterProps, MultiSelectFilterProps, SingleSelectFilterProps} from '../filter-types';

/**
 * Configuration for creating filter props
 */
export interface FilterConfig<T = any> {
    label: string;
    data: T[];
    value: string;
    onChange: (value: string) => void;
    children: [(item: T, i: number) => string, (item: T, i: number) => ReactNode];
    spinner?: boolean;
    disabled?: boolean;
    itemToString?: (item: T, i: number) => string;
    testId?: string;
}

/**
 * Multi-select configuration options
 */
export interface MultiSelectConfig {
    enabled: boolean;
    maxSelections?: number;
    sortSelectedFirst?: boolean;
    showPrimaryIndicator?: boolean;
    hideTags?: boolean;
}

/**
 * Creates filter props with optional multi-select configuration
 *
 * @param config - Base filter configuration
 * @param multiSelectConfig - Multi-select configuration (if enabled)
 * @returns FilterProps (either single-select or multi-select)
 */
export function createFilterProps<T = any>(
    config: FilterConfig<T>,
    multiSelectConfig?: MultiSelectConfig
): FilterProps {
    const baseProps = {
        label: config.label,
        data: config.data,
        value: config.value,
        onChange: config.onChange,
        children: config.children,
        spinner: config.spinner,
        disabled: config.disabled,
        itemToString: config.itemToString,
        ['data-testid']: config.testId,
    };

    if (multiSelectConfig?.enabled) {
        const multiSelectProps: MultiSelectFilterProps = {
            ...baseProps,
            enableMultiSelect: true,
            maxSelections: multiSelectConfig.maxSelections,
            sortSelectedFirst: multiSelectConfig.sortSelectedFirst ?? true,
            showPrimaryIndicator: multiSelectConfig.showPrimaryIndicator ?? true,
            hideTags: multiSelectConfig.hideTags ?? false,
        };
        return multiSelectProps;
    }

    const singleSelectProps: SingleSelectFilterProps = {
        ...baseProps,
        enableMultiSelect: false,
    };
    return singleSelectProps;
}
